import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from "react";

import { Box, Button, CircularProgress } from "@material-ui/core";
import { AddCircleOutline } from "@material-ui/icons";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import { AuthContext } from "../../context/Auth/AuthContext";
import useTickets from "../../hooks/useTickets";
import {
	connectSocket,
	addGoWebSocketMessageHandler,
	addGoWebSocketOpenHandler,
	getGoWebSocket,
} from "../../services/socketv2.js";
import { countUnreadConversations } from "../../helpers/countUnreadConversations";
import {
	belongsInTicketTab,
	isStatusMessage,
	isStatusTicket,
} from "../../helpers/isStatusTicket";
import { sortTicketsByLastActivity } from "../../helpers/ticketListSort";
import { ticketStatusSocketRoom } from "../../helpers/ticketSocketRoom";
import { TICKET_REALTIME } from "../../helpers/ticketRealtimeEvents";
import { i18n } from "../../translate/i18n";
import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";
import TailwickTicketsListView from "../../pages/Tickets/TailwickTicketsListView";
import TailwickStatusListView from "../../pages/Tickets/TailwickStatusListView";
import StatusListItem from "../Status/StatusListItem";
import { normalizeContactTags } from "../../helpers/normalizeContactTags";
import { useStyles } from "./styles";

function mapContactTags(contact) {
	return normalizeContactTags(contact);
}

function mergeTicket(existing, incoming) {
	const normalized = { ...incoming };
	if (normalized.contact) {
		normalized.contact = mapContactTags(normalized.contact);
	}

	if (!existing) {
		return normalized;
	}

	const mergedContact = normalized.contact
		? mapContactTags({
				...(existing.contact || {}),
				...normalized.contact,
				tags:
					normalized.contact.tags?.length > 0
						? normalized.contact.tags
						: existing.contact?.tags || [],
				// Evita reaproveitar contactTags antigos em cima dos novos tags
				contactTags:
					normalized.contact.contactTags?.length > 0
						? normalized.contact.contactTags
						: normalized.contact.tags?.length > 0
							? []
							: existing.contact?.contactTags || [],
			})
		: existing.contact;

	const merged = {
		...existing,
		...normalized,
		contact: mergedContact,
		lastMessageObj:
			normalized.lastMessageObj ?? existing.lastMessageObj,
		user: normalized.user ?? existing.user,
		whatsapp: normalized.whatsapp ?? existing.whatsapp,
		queue: normalized.queue ?? existing.queue,
	};
	// Payload incompleto não pode apagar contactId (agrupa errado e some da lista).
	if (normalized.contactId == null && existing.contactId != null) {
		merged.contactId = existing.contactId;
	}
	return merged;
}

function sameTicketId(a, b) {
	if (a == null || b == null) return false;
	return String(a) === String(b);
}

function sameUserId(a, b) {
	if (a == null || b == null) return false;
	return String(a) === String(b);
}

function upsertTicketInList(state, ticket) {
	const next = [...state];
	const ticketIndex = next.findIndex((t) => sameTicketId(t.id, ticket.id));
	if (ticketIndex !== -1) {
		next[ticketIndex] = mergeTicket(next[ticketIndex], ticket);
	} else {
		next.push(ticket);
	}
	return sortTicketsByLastActivity(next, "desc");
}

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const { tickets: newTickets, replace } = action.payload;
		let nextState = replace ? [] : [...state];

		newTickets.forEach((ticket) => {
			const index = nextState.findIndex((t) => sameTicketId(t.id, ticket.id));

			if (index !== -1) {
				nextState[index] = mergeTicket(nextState[index], ticket);
			} else {
				nextState.push(ticket);
			}
		});

		return sortTicketsByLastActivity(nextState, "desc");
	}
	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		return state.map((t) =>
			sameTicketId(t.id, ticketId) ? { ...t, unreadMessages: 0 } : t
		);
	}

	if (action.type === "UPDATE_TICKET") {
		return upsertTicketInList(state, action.payload);
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		return upsertTicketInList(state, action.payload);
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = mapContactTags(action.payload);
		return state.map((t) =>
			t.contactId === contact.id ? { ...t, contact } : t
		);
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		return state.filter((t) => String(t.id) !== String(ticketId));
	}

	if (action.type === "RESET") {
		return [];
	}
	return state;
};

export default function TicketsList(props) {
	const {
		status,
		searchParam,
		showAll,
		selectedQueueIds,
		updateCount,
		sortBy,
		style,
		searched,
		showUnread,
		tags,
		tailwickLayout = false,
		reportUnreadBadgeCount,
		channels,
		bulkSelection,
		setBulkSelection,
		bulkSelected = [],
		setBulkSelected
	} = props;
	const effectiveSortBy = sortBy || "desc";
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const { user } = useContext(AuthContext);
	const [final, setFinal] = useState(false);
	const pendingTicketUpdatesRef = useRef(new Map());
	const ticketUpdateTimersRef = useRef(new Map());
	const showUnreadRef = useRef(showUnread);
	const showAllRef = useRef(showAll);
	const [reloadTicktList, setReloadTicktList] = useState(0)

	useEffect(() => {
		showUnreadRef.current = showUnread;
	}, [showUnread]);

	useEffect(() => {
		showAllRef.current = showAll;
	}, [showAll]);

	useEffect(() => {
		setPageNumber(1);
	}, [status, searched, dispatch, showAll, selectedQueueIds, showUnread, tags, channels]);

	function verifyTicketByTags(ticket, allowedTags = []) {
		if (!allowedTags?.length) return true;

		const allowed = allowedTags.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
		const tagIds = [];

		(ticket.contact?.tags || []).forEach((tag) => {
			const id = tag?.id ?? tag?.tagId ?? tag?.ContactTag?.tagId;
			if (id != null) tagIds.push(Number(id));
		});

		(ticket.contact?.contactTags || []).forEach((ct) => {
			const id = ct?.tagId ?? ct?.tag?.id ?? ct?.Tag?.id;
			if (id != null) tagIds.push(Number(id));
		});

		return tagIds.some((id) => allowed.includes(id));
	}

	const queueIdInSelected = useCallback((queueId, selectedIds) => {
		if (queueId == null || !Array.isArray(selectedIds) || selectedIds.length === 0) {
			return false;
		}
		const q = Number(queueId);
		if (Number.isNaN(q)) return false;
		return selectedIds.some((id) => Number(id) === q);
	}, []);

	// Igual à API Go: se o filtro da UI está vazio, usa as filas do usuário.
	// Sem isso, selectedQueueIds=[] liberava TODOS os setores no realtime (open e pending).
	const effectiveQueueIds = useMemo(() => {
		if (Array.isArray(selectedQueueIds) && selectedQueueIds.length > 0) {
			return selectedQueueIds;
		}
		const fromUser = (user?.queues || [])
			.map((q) => q?.id)
			.filter((id) => id != null);
		return fromUser;
	}, [selectedQueueIds, user?.queues]);

	const filterTickets = useCallback((tickts) => {
		const canSeeAll = !!user?.canSeeAllTickets;

		const filtro = (ticket) => {
			if (status === "status") {
				if (!belongsInTicketTab(ticket, status)) return false;
			} else if (isStatusTicket(ticket)) {
				return false;
			}

			if (status === "status") {
				if (tags?.length && !verifyTicketByTags(ticket, tags)) return false;
				return true;
			}

			if (status === "open" && ticket.status !== "open") return false;
			if (status === "pending" && ticket.status !== "pending") return false;
			if (status === "closed" && ticket.status !== "closed") return false;

			const hasQueueFilter =
				Array.isArray(effectiveQueueIds) && effectiveQueueIds.length > 0;

			const hasChannelFilter =
				Array.isArray(channels) && channels.length > 0;

			// open + pending: sempre respeitar fila efetiva (UI ou filas do user).
			if (hasQueueFilter) {
				if (!canSeeAll) {
					if (
						ticket.queueId == null ||
						!queueIdInSelected(ticket.queueId, effectiveQueueIds)
					) {
						return false;
					}
				} else if (
					ticket.queueId != null &&
					!queueIdInSelected(ticket.queueId, effectiveQueueIds)
				) {
					return false;
				}
			}

			if (hasChannelFilter && !channels.includes(ticket.channel)) {
				return false;
			}

			if (tags?.length && !verifyTicketByTags(ticket, tags)) return false;

			if (showUnread === "grupos" && !ticket.contact?.isGroup) return false;

			return true;
		};

		if (Array.isArray(tickts)) {
			return tickts.filter((ticket) => filtro(ticket));
		}
		if (filtro(tickts)) {
			return tickts;
		}
		return [];
	}, [
		user?.canSeeAllTickets,
		status,
		tags,
		effectiveQueueIds,
		showUnread,
		channels,
		queueIdInSelected,
	]);

	const filterTicketsRef = useRef(filterTickets);
	useEffect(() => {
		filterTicketsRef.current = filterTickets;
	}, [filterTickets]);

	const cancelQueuedTicketUpdate = useCallback((ticketId) => {
		if (ticketId == null) return;
		pendingTicketUpdatesRef.current.delete(ticketId);
		const timer = ticketUpdateTimersRef.current.get(ticketId);
		if (timer) {
			clearTimeout(timer);
			ticketUpdateTimersRef.current.delete(ticketId);
		}
	}, []);

	const queueTicketUpdate = useCallback((ticket) => {
		const ticketId = ticket.id;
		// Gate único: nunca upsertar ticket que a API/lista filtraria (flash financeiro→suporte).
		const allowed = filterTicketsRef.current([ticket]);
		if (!allowed.length) {
			cancelQueuedTicketUpdate(ticketId);
			dispatch({ type: "DELETE_TICKET", payload: ticketId });
			return;
		}

		// No filtro "não lidas", ticket sem unread não deve voltar via debounce.
		if (showUnreadRef.current === true && !(Number(ticket.unreadMessages) > 0)) {
			cancelQueuedTicketUpdate(ticketId);
			dispatch({ type: "DELETE_TICKET", payload: ticketId });
			return;
		}

		pendingTicketUpdatesRef.current.set(ticketId, ticket);

		const existingTimer = ticketUpdateTimersRef.current.get(ticketId);
		if (existingTimer) {
			clearTimeout(existingTimer);
		}

		ticketUpdateTimersRef.current.set(
			ticketId,
			setTimeout(() => {
				ticketUpdateTimersRef.current.delete(ticketId);
				const latest = pendingTicketUpdatesRef.current.get(ticketId);
				if (!latest) return;
				pendingTicketUpdatesRef.current.delete(ticketId);
				const stillAllowed = filterTicketsRef.current([latest]);
				if (!stillAllowed.length) {
					dispatch({ type: "DELETE_TICKET", payload: ticketId });
					return;
				}
				if (
					showUnreadRef.current === true &&
					!(Number(latest.unreadMessages) > 0)
				) {
					dispatch({ type: "DELETE_TICKET", payload: ticketId });
					return;
				}
				dispatch({ type: "UPDATE_TICKET", payload: latest });
			}, 100)
		);
	}, [cancelQueuedTicketUpdate]);

	const { tickets, hasMore, loading, count: ticketsApiCount } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		showUnread,
		queueIds: JSON.stringify(selectedQueueIds),
		tagsIds: JSON.stringify(tags),
		channels: JSON.stringify(channels),
		reloadTicktList,
	});

	useEffect(() => {
		if (!status && !searchParam) return;

		const filteredTickets = filterTickets(tickets);

		dispatch({
			type: "LOAD_TICKETS",
			payload: {
				tickets: filteredTickets,
				replace: pageNumber === 1,
			},
		});
	}, [
		tickets,
		status,
		searchParam,
		selectedQueueIds,
		showUnread,
		pageNumber,
		tags,
		filterTickets,
		channels,
	]);

	useEffect(() => {
		if (!user?.id) return;

		const timers = ticketUpdateTimersRef.current;
		const pendingUpdates = pendingTicketUpdatesRef.current;
		const room = ticketStatusSocketRoom(user?.companyId, status);

		const hasQueueFilter =
			Array.isArray(effectiveQueueIds) && effectiveQueueIds.length > 0;

		const notBelongsToUserQueues = (ticket) =>
			hasQueueFilter &&
			ticket.queueId != null &&
			!queueIdInSelected(ticket.queueId, effectiveQueueIds);

		const sameCompany = (ticket) => {
			if (user?.companyId == null) return false;
			if (ticket?.companyId == null) return false;
			return String(ticket.companyId) === String(user.companyId);
		};

		const isMyTicket = (ticket) => sameUserId(ticket.userId, user?.id);

		// Alinhado à API: fila efetiva SEMPRE; open sem showAll exige ser meu ticket.
		let canUpdateTicket = () => false;
		if (status === "status") {
			canUpdateTicket = (ticket) =>
				sameCompany(ticket) && belongsInTicketTab(ticket, status);
		} else {
			canUpdateTicket = (ticket) => {
				if (!sameCompany(ticket) || !belongsInTicketTab(ticket, status)) {
					return false;
				}
				if (hasQueueFilter) {
					if (user?.canSeeAllTickets || user?.profile === "admin") {
						if (
							ticket.queueId != null &&
							!queueIdInSelected(ticket.queueId, effectiveQueueIds)
						) {
							return false;
						}
					} else if (
						ticket.queueId == null ||
						!queueIdInSelected(ticket.queueId, effectiveQueueIds)
					) {
						return false;
					}
				}
				if (
					status === "open" &&
					!showAllRef.current &&
					!user?.canSeeAllTickets &&
					user?.profile !== "admin" &&
					!isMyTicket(ticket)
				) {
					return false;
				}
				return true;
			};
		}

		const buildTicketPayload = (data) => {
			if (!data?.ticket) return null;
			const ticket = data.message
				? { ...data.ticket, lastMessageObj: data.message }
				: { ...data.ticket };
			if (ticket.contact) {
				ticket.contact = mapContactTags(ticket.contact);
			}
			return ticket;
		};

		const belongsToThisStatusList = (ticket) =>
			belongsInTicketTab(ticket, status);

		const ticketMatchesThisList = (ticket) => {
			if (!ticket) return false;
			return (
				String(ticket.status || "").toLowerCase() ===
				String(status || "").toLowerCase()
			);
		};

		const applyTicketEvent = (data, { allowCreate = true } = {}) => {
			if (!allowCreate && data.action === "create") {
				return;
			}

			const ticket = buildTicketPayload(data);

			if (!ticket) {
				if (data.action === "delete" && data.ticketId) {
					dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
				}
				return;
			}

			// Nunca processar ticket de outra empresa (sala notification vazava cross-tenant).
			if (!sameCompany(ticket)) {
				return;
			}

			const action = data.action;

			if (
				action === "update" &&
				(!ticketMatchesThisList(ticket) || !belongsInTicketTab(ticket, status))
			) {
				dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				return;
			}

			if (!belongsToThisStatusList(ticket)) {
				if (action === "delete") {
					dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				}
				return;
			}

			const shouldUpdate = canUpdateTicket(ticket);

			// Marcar como lido: só zera unread. Remover da lista SOMENTE no filtro "não lidas".
			if (action === "updateUnread") {
				if (!ticketMatchesThisList(ticket)) {
					return;
				}
				const ticketId = data.ticketId ?? ticket.id;
				cancelQueuedTicketUpdate(ticketId);
				dispatch({
					type: "RESET_UNREAD",
					payload: ticketId,
				});
				if (showUnreadRef.current === true) {
					dispatch({ type: "DELETE_TICKET", payload: ticketId });
				}
				return;
			}

			if (action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				return;
			}

			if (action === "create") {
				if (
					shouldUpdate &&
					ticketMatchesThisList(ticket) &&
					belongsInTicketTab(ticket, status)
				) {
					queueTicketUpdate(ticket);
				}
				return;
			}

			if (action !== "update") {
				return;
			}

			if (
				hasQueueFilter &&
				ticket.queueId != null &&
				!queueIdInSelected(ticket.queueId, effectiveQueueIds)
			) {
				dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				return;
			}

			if (!belongsInTicketTab(ticket, status) || !ticketMatchesThisList(ticket)) {
				dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				return;
			}

			if (showUnreadRef.current === true && !(Number(ticket.unreadMessages) > 0)) {
				cancelQueuedTicketUpdate(ticket.id);
				dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				return;
			}

			// "Meus tickets": reatribuído a outro → remove.
			// canSeeAllTickets mantém tickets de outros na lista mesmo com showAll off.
			if (
				!showAllRef.current &&
				!user?.canSeeAllTickets &&
				ticket.status === "open" &&
				ticket.userId != null &&
				!sameUserId(ticket.userId, user?.id)
			) {
				cancelQueuedTicketUpdate(ticket.id);
				dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				return;
			}

			if (
				notBelongsToUserQueues(ticket) &&
				!sameUserId(ticket.userId, user?.id) &&
				(status === "pending" || showAllRef.current || !ticket.userId)
			) {
				cancelQueuedTicketUpdate(ticket.id);
				dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				return;
			}

			if (!shouldUpdate) return;

			queueTicketUpdate(ticket);
		};

		// Lista de tickets: realtime só via Go WS.
		// Socket.IO do Node ainda emite para company-* e causava flash cross-setor.
		const handleGoSocketMessage = (event) => {
			let data;
			try {
				data = JSON.parse(event.data);
			} catch {
				return;
			}
			if (!data || !status) return;
			if (data.message && !data.ticket) return;

			// Eco de envio no WS Go: mesma regra do socket.io (sair de não lidas).
			if (
				data.action === "create" &&
				data.message?.fromMe &&
				data.ticket
			) {
				if (!sameCompany(data.ticket)) return;
				if (String(data.ticket.status || "").toLowerCase() !== String(status || "").toLowerCase()) {
					return;
				}
				const ticketId = data.ticket.id;
				cancelQueuedTicketUpdate(ticketId);
				dispatch({ type: "RESET_UNREAD", payload: ticketId });
				if (showUnreadRef.current === true) {
					dispatch({ type: "DELETE_TICKET", payload: ticketId });
				} else {
					queueTicketUpdate({
						...data.ticket,
						lastMessageObj: data.message,
						unreadMessages: 0,
					});
				}
				return;
			}

			// Mensagem inbound no WS Go: espelha appMessage (só na aba do status correto).
			// NÃO usar showAll/pending solto aqui — isso flashava tickets de outros setores.
			// queueTicketUpdate já aplica filterTickets (fila/canal/tags).
			if (
				data.action === "create" &&
				data.message &&
				!data.message.fromMe &&
				data.ticket
			) {
				if (!sameCompany(data.ticket)) return;
				if (String(data.ticket.status || "").toLowerCase() !== String(status || "").toLowerCase()) {
					return;
				}
				if (status !== "status" && isStatusMessage(data.message)) return;
				if (!belongsToThisStatusList(data.ticket)) return;
				if (!canUpdateTicket(data.ticket)) return;

				const ticket = {
					...data.ticket,
					lastMessageObj: data.message,
					unreadMessages: Math.max(
						1,
						Number(data.ticket.unreadMessages) || 0
					),
				};
				if (ticket.contact) {
					ticket.contact = mapContactTags(ticket.contact);
				}
				if (showUnreadRef.current === true && !(ticket.unreadMessages > 0)) {
					ticket.unreadMessages = 1;
				}
				queueTicketUpdate(ticket);
				return;
			}

			applyTicketEvent(data, { allowCreate: false });
		};

		const joinGoRoom = () => {
			if (!room) return;
			const s = getGoWebSocket();
			if (!s || s.readyState !== WebSocket.OPEN) return;
			s.send(JSON.stringify({ action: "joinTickets", room }));
		};

		const removeGoMessage = addGoWebSocketMessageHandler(
			handleGoSocketMessage
		);
		const removeGoOpen = addGoWebSocketOpenHandler(joinGoRoom);

		if (status) {
			connectSocket({
				userId: user.id,
				companyId: user.companyId,
			});
			joinGoRoom();
		}

		const onTicketRealtime = (event) => {
			const data = event?.detail;
			if (!data?.ticket) return;
			applyTicketEvent(data, { allowCreate: true });
		};
		window.addEventListener(TICKET_REALTIME, onTicketRealtime);

		return () => {
			window.removeEventListener(TICKET_REALTIME, onTicketRealtime);
			removeGoMessage();
			removeGoOpen();
			timers.forEach((timer) => clearTimeout(timer));
			timers.clear();
			pendingUpdates.clear();
			if (room) {
				const s = getGoWebSocket();
				if (s && s.readyState === WebSocket.OPEN) {
					s.send(
						JSON.stringify({
							action: "leaveTickets",
							room,
						})
					);
				}
			}
		};
	}, [
		status,
		showAll,
		showUnread,
		user?.id,
		user?.companyId,
		user?.profile,
		user?.canSeeAllTickets,
		effectiveQueueIds,
		queueTicketUpdate,
		cancelQueuedTicketUpdate,
		queueIdInSelected,
	]);

	useEffect(() => {
		if (typeof reportUnreadBadgeCount !== "function" || loading) return;
		reportUnreadBadgeCount(countUnreadConversations(ticketsList));
	}, [ticketsList, loading, reportUnreadBadgeCount]);

	useEffect(() => {
		if (typeof updateCount !== "function") return;

		const total =
			ticketsApiCount != null && ticketsApiCount !== ""
				? Number(ticketsApiCount)
				: ticketsList.length;

		if (Number.isNaN(total) || total < 0) {
			updateCount(0);
			return;
		}

		if (hasMore && total > 0) {
			updateCount(`${total}+`);
		} else {
			updateCount(total);
		}
	}, [ticketsList, ticketsApiCount, hasMore, updateCount]);

	const loadMore = () => {
		setPageNumber((prevState) => prevState + 1);
		setFinal(false)

	};

	const reload = () => {
		setReloadTicktList(prev => prev + 1);
	}

	const handleScroll = (e) => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			setFinal(true)

		}
	};


	const sortedTickets = sortTicketsByLastActivity(ticketsList, effectiveSortBy);

	const ticketMap = new Map();

	sortedTickets.forEach(ticket => {
		const { contactId, whatsappId, lastMessageObj } = ticket;
		// Mesmo número em conexões diferentes = tickets distintos (não aninhar).
		const groupKey = `${contactId ?? "c"}:${whatsappId ?? "w"}`;
		const mediaUrl = lastMessageObj?.mediaUrl;

		if (!ticketMap.has(groupKey)) {
			ticketMap.set(groupKey, {
				...ticket,
				relatedTickets: [ticket],
				mediaUrls: mediaUrl ? [mediaUrl] : []
			});
		} else {
			const existing = ticketMap.get(groupKey);
			existing.relatedTickets.push(ticket);
			if (mediaUrl) {
				existing.mediaUrls.push(mediaUrl);
			}
		}
	});

	const groupedTickets = Array.from(ticketMap.values()).filter((ticket) =>
		status === "status"
			? belongsInTicketTab(ticket, status)
			: !isStatusTicket(ticket)
	);

	if (tailwickLayout && status === "status") {
		return (
			<TailwickStatusListView
				tickets={groupedTickets}
				loading={loading}
				final={final}
				loadMore={loadMore}
				onScroll={handleScroll}
			/>
		);
	}

	if (tailwickLayout) {
		return (
			<TailwickTicketsListView
				tickets={groupedTickets}
				status={status}
				loading={loading}
				final={final}
				loadMore={loadMore}
				onScroll={handleScroll}
				updateCount={updateCount}
				bulkSelection={bulkSelection}
				setBulkSelection={setBulkSelection}
				bulkSelected={bulkSelected}
				setBulkSelected={setBulkSelected}
				queueIds={ selectedQueueIds }
				tagsIds={ tags }
				channels={channels}
				companyId={user?.companyId}
				canSeeAllTickets={user?.canSeeAllTickets}
				sortBy={sortBy}
				setPageNumber={setPageNumber}
				reload={reload}
			/>
		);
	}

	return (
		<Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{sortedTickets.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<Box overflow="auto" style={{ maxHeight: "100%" }}>
							{groupedTickets.map((ticket) =>
								status === "status" ? (
									<StatusListItem
										key={ticket.id}
										ticket={ticket}
										relatedTickets={ticket.relatedTickets}
									/>
								) : (
									<TicketListItem
										key={ticket.id}
										ticket={ticket}
										updateCount={updateCount}
										relatedTickets={ticket.relatedTickets}
										bulkSelection={bulkSelection}
										setBulkSelectio={setBulkSelection}
										bulkSelected={bulkSelected}
										setBulkSelected={setBulkSelected}
									/>
								)
							)}
						</Box>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
				{final && (
					<Button
						variant="contained"
						color="primary"
						size="medium"
						startIcon={!loading && <AddCircleOutline />}
						onClick={loadMore}
						disabled={loading}
						style={{
							margin: "16px auto",
							display: "flex",
							fontWeight: 600,
							borderRadius: 20,
							padding: "8px 24px",
							textTransform: "none",
						}}
					>
						{loading ? <CircularProgress size={24} color="inherit" /> : "Carregar mais"}
					</Button>
				)}


				<div className={classes.msgFinal}><p><svg xmlns="http://www.w3.org/2000/svg" fill="gray" width={"10px"} viewBox="0 0 448 512"><path d="M144 144l0 48 160 0 0-48c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192l0-48C80 64.5 144.5 0 224 0s144 64.5 144 144l0 48 16 0c35.3 0 64 28.7 64 64l0 192c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 256c0-35.3 28.7-64 64-64l16 0z" /></svg> Suas mensagens pessoais são protegidas com a <span style={{ color: "green" }}> criptografia de ponta a ponta</span></p>
				</div>
			</Paper>
		</Paper>
	);
}

